import { useContext } from '@berlin-front/common/composables/context';
import { reactive, computed, nextTick, ref, watch } from '@vue/composition-api';
import { useRouter } from '@berlin-front/common/composables/useRouter';
import { useRoute } from '@berlin-front/common/composables/useRoute';
import { ApiCacheContext } from '@/services/ApiCacheContext';
import { ROUTE_NAME } from '@/router/route-name';
import { formatNumber } from '@berlin-front/common/utils';
import { POINT_USE_TYPE } from '@/entity/pointUseType';
import { AMOUNT_DISPLAY_TYPE } from '@/entity/amountDisplayType';
import { AtonePaymentPointUseTypeEnum } from '@netprotections/berlin-bff';
import { OneTimePaymentContextType } from './context';

export default {
  useOneTimePayment: (): ReturnType<OneTimePaymentContextType['useOneTimePayment']> => {
    const router = useRouter();
    const route = useRoute();
    const { atoneShopV2OptionGetResult, atonePointGetResult, atoneModalParameter, userFullName, atoneMethodGetResult } =
      useContext(ApiCacheContext);
    const pointUseType = computed(
      () => atoneMethodGetResult.value?.atonePayment?.pointUseType ?? POINT_USE_TYPE.DISABLED,
    );
    const amountDisplayType = computed(
      () => atoneShopV2OptionGetResult.value?.atoneServiceOption.amountDisplayType ?? AMOUNT_DISPLAY_TYPE.NORMAL,
    );
    const itemTotalPrice = computed(() => atoneModalParameter.value?.payment.amount ?? 0);
    const ownPoint = computed(() => atonePointGetResult.value?.point || 0);
    const maxUsablePoint = computed(() => Math.min(Math.max(0, ownPoint.value || 0), itemTotalPrice.value));

    // 状態
    const alert = ref([route.query.alert ?? []].flat().flatMap((v) => (v === null ? [] : [v])));
    const state = reactive({
      point: 0 as number | '',
    });
    const onInput = (value: string) => {
      if (value.length === 0) {
        state.point = '';
      } else if (/^\d{1,10}$/.test(value)) {
        state.point = Number.parseInt(value, 10);
      } else {
        state.point = (state.point || 0) + 1;
        void nextTick().then(() => {
          state.point = (state.point || 0) - 1;
        });
      }
    };
    const onBlur = () => {
      if (state.point === '' || state.point < 0) {
        state.point = 0;
      } else if (state.point > maxUsablePoint.value) {
        state.point = maxUsablePoint.value;
      }
    };
    watch(
      () => state.point,
      () => {
        if (alert.value.length > 0) {
          alert.value = [];
        }
      },
    );

    // 表示項目
    const shopName = computed(() => atoneShopV2OptionGetResult.value?.shopName ?? '');
    const retentionPoint = computed(() => atonePointGetResult.value?.point ?? 0);
    const subtractPoint = computed(() => {
      if (state.point === '' || Number.isNaN(state.point) || state.point < 0) {
        return '0';
      }
      return formatNumber(Math.min(state.point || 0, maxUsablePoint.value));
    });
    const totalAmount = computed(() => itemTotalPrice.value - Math.min(state.point || 0, maxUsablePoint.value));
    const hasPoint = computed(() => ownPoint.value > 0 && pointUseType.value === AtonePaymentPointUseTypeEnum.Enable);
    // ポイントシステムでエラーが発生してるとき（pointは負数。）
    const npcSystemHasError = computed(
      () => ownPoint.value < 0 && pointUseType.value === AtonePaymentPointUseTypeEnum.Enable,
    );

    // ユーザ名（フッターにユーザ名と「ログアウト」ボタンを表示する）
    const memberName = userFullName;

    // 分割払いが利用できるかどうか
    const isSplitAvailable =
      !atoneShopV2OptionGetResult.value?.atoneSplitServiceOption.errors &&
      atoneMethodGetResult.value?.splitPayment?.usablePatterns;

    // 分割払いについてのメッセージ
    const splitMessages = isSplitAvailable
      ? // 分割払いが利用できる場合
        ['※ご注文の確定後は、支払い回数を変更できません']
      : // 分割払いが利用できない場合
        ['※この買い物では分割払いはご利用いただけません'];

    // 「ご注文を確定」押下時のアクション
    const onSubmit = () => {
      void router.replace({ name: ROUTE_NAME.ATONE_LOADING_SETTLEMENT, params: { point: state.point.toString() } });
    };

    // 「戻る」押下時のアクション
    // 分割払いが利用できる場合は支払い回数選択画面に遷移
    // 分割払いが利用できない場合はボタンは非表示
    const goBack = isSplitAvailable
      ? () => {
          void router.replace({ name: ROUTE_NAME.ATONE_SPLIT_SELECT });
        }
      : undefined;

    return reactive({
      // 状態
      alert,
      state,
      pointUseType,
      amountDisplayType,
      // 表示項目
      shopName,
      retentionPoint,
      subtractPoint,
      itemTotalPrice,
      totalAmount,
      hasPoint,
      npcSystemHasError,
      splitMessages,
      memberName,
      onBlur,

      // action
      onSubmit,
      goBack,

      onInput,
    });
  },
} satisfies OneTimePaymentContextType;
